/**
 * Fechas de calendario sin hora ni zona horaria ("2026-07-31").
 *
 * Las fechas de vencimiento, de pago y de desembolso son *días de calendario*
 * en Guatemala, no instantes. Representarlas como cadenas `YYYY-MM-DD` evita
 * el error clásico en que `new Date().toISOString()` devuelve "mañana"
 * después de las 6 p. m. en Guatemala (UTC-6). Toda la aritmética ocurre en
 * UTC para que la zona horaria del host nunca se filtre.
 */
/** Patrón `AAAA-MM-DD` con grupos para año, mes y día. */
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Milisegundos en un día (24 × 60 × 60 × 1000). */
const MS_PER_DAY = 86_400_000;
/** Meses en un año. */
const MONTHS_PER_YEAR = 12;
/** Longitud de `YYYY-MM-DD` dentro de una cadena ISO completa. */
const ISO_DATE_LENGTH = 10;
/** Longitud del prefijo `YYYY-MM` (año y mes). */
const ISO_YEAR_MONTH_LENGTH = 7;
/** Indica si la cadena es una fecha `YYYY-MM-DD` real (rechaza 2026-02-30). */
export function isIsoDate(value) {
    const match = ISO_DATE_PATTERN.exec(value);
    if (!match)
        return false;
    const [, y, m, d] = match;
    const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
    return date.toISOString().startsWith(value);
}
/** Valida y marca una cadena `YYYY-MM-DD`. */
export function isoDate(value) {
    if (!isIsoDate(value))
        throw new RangeError(`Fecha inválida: "${value}" (se espera AAAA-MM-DD)`);
    return value;
}
/** Convierte un `Date` (interpretado en UTC) a su fecha de calendario. */
export function toIsoDate(date) {
    return date.toISOString().slice(0, ISO_DATE_LENGTH);
}
/** Medianoche UTC de la fecha de calendario. */
function toUtcDate(value) {
    return new Date(`${value}T00:00:00.000Z`);
}
/** Suma (o resta, con negativos) días de calendario. */
export function addDays(value, days) {
    return toIsoDate(new Date(toUtcDate(value).getTime() + days * MS_PER_DAY));
}
/** Días del mes (`monthIndex` base 0): el día 0 del mes siguiente es el último de este. */
function daysInMonth(year, monthIndex) {
    return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}
/**
 * Suma meses de calendario conservando el día ancla y recortando al fin de
 * mes: 31 ene + 1 mes = 28 feb (no 3 mar, como daría `setUTCMonth`).
 *
 * `anchorDay` permite que un calendario vuelva al día 31 tras pasar por
 * febrero: se pasa el día del primer vencimiento.
 */
export function addMonths(value, months, anchorDay) {
    const base = toUtcDate(value);
    const totalMonths = base.getUTCFullYear() * MONTHS_PER_YEAR + base.getUTCMonth() + months;
    const year = Math.floor(totalMonths / MONTHS_PER_YEAR);
    const monthIndex = totalMonths % MONTHS_PER_YEAR;
    const day = Math.min(anchorDay ?? base.getUTCDate(), daysInMonth(year, monthIndex));
    return toIsoDate(new Date(Date.UTC(year, monthIndex, day)));
}
/** Días completos de `from` a `to` (negativo cuando `to` es anterior). */
export function daysBetween(from, to) {
    return Math.round((toUtcDate(to).getTime() - toUtcDate(from).getTime()) / MS_PER_DAY);
}
/** Comparador para `sort`: -1, 0 o 1 (el orden lexicográfico coincide con el cronológico). */
export function compareIsoDates(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** La más reciente de dos fechas. */
export function maxIsoDate(a, b) {
    return a >= b ? a : b;
}
/** Día de la semana: 0 = domingo … 6 = sábado. */
export function dayOfWeek(value) {
    return toUtcDate(value).getUTCDay();
}
/** Día del mes (1–31). */
export function dayOfMonth(value) {
    return toUtcDate(value).getUTCDate();
}
/** Primer día del mes de la fecha dada. */
export function startOfMonth(value) {
    return `${value.slice(0, ISO_YEAR_MONTH_LENGTH)}-01`;
}
/** Último día del mes de la fecha dada. */
export function endOfMonth(value) {
    return addDays(addMonths(startOfMonth(value), 1), -1);
}
//# sourceMappingURL=iso-date.js.map